import { BROWSER_LOCAL_CACHE_DEFAULT_API_TTL_MS } from './config'
import { assertPayloadCacheable, classifyLocalCacheSensitivity, sanitizeLocalCachePayload } from './sensitivity'
import type {
  BrowserLocalCacheDriver,
  BrowserLocalCacheNamespace,
  BrowserLocalCachePutOptions,
  BrowserLocalCacheRecord,
  BrowserLocalCacheStoreName,
} from './types'

const estimatePayloadSize = (payload: unknown): number => {
  if (payload instanceof Blob) return payload.size
  if (payload instanceof ArrayBuffer) return payload.byteLength
  try {
    return new Blob([JSON.stringify(payload)]).size
  } catch {
    return 0
  }
}

export const buildLocalCacheRecord = <TPayload>(
  namespace: BrowserLocalCacheNamespace,
  cacheKey: string,
  resourceName: string,
  payload: TPayload,
  options: BrowserLocalCachePutOptions
): BrowserLocalCacheRecord<TPayload> => {
  const now = options.now ?? Date.now()
  const sensitivity = options.sensitivity ?? classifyLocalCacheSensitivity(resourceName)
  if (sensitivity === 'forbidden') {
    throw new Error(`Resource is forbidden for browser local cache: ${resourceName}`)
  }
  assertPayloadCacheable(resourceName, payload)
  const sanitizedPayload = sanitizeLocalCachePayload(payload)
  return {
    cacheKey,
    userScope: namespace.userScope,
    environmentScope: `${namespace.environment}:${namespace.apiBaseHash}`,
    resourceName,
    route: options.route,
    entityType: options.entityType,
    sensitivity,
    encryption: options.encryption ?? (sensitivity === 'public' || sensitivity === 'low' ? 'plain' : 'encrypted'),
    etag: options.etag,
    lastModified: options.lastModified,
    serverRevision: options.serverRevision,
    projectionVersion: options.projectionVersion,
    savedAt: now,
    updatedAt: now,
    expiresAt: now + Math.max(0, options.ttlMs),
    lastAccessedAt: now,
    byteSize: options.byteSize ?? estimatePayloadSize(sanitizedPayload),
    tags: options.tags,
    payload: sanitizedPayload,
  }
}

export class BrowserLocalCacheStore {
  constructor(
    private readonly driver: BrowserLocalCacheDriver,
    private readonly namespace: BrowserLocalCacheNamespace
  ) {}

  async get<TPayload = unknown>(
    storeName: BrowserLocalCacheStoreName,
    key: string,
    now = Date.now()
  ): Promise<BrowserLocalCacheRecord<TPayload> | null> {
    const record = await this.driver.get<TPayload>(storeName, key)
    if (!record) return null
    if (record.userScope !== this.namespace.userScope) return null
    if (record.tombstone || record.expiresAt <= now) {
      await this.driver.delete(storeName, key)
      return null
    }
    const nextRecord: BrowserLocalCacheRecord<TPayload> = {
      ...record,
      lastAccessedAt: now,
    }
    await this.driver.put(storeName, key, nextRecord)
    return nextRecord
  }

  async put<TPayload = unknown>(
    storeName: BrowserLocalCacheStoreName,
    key: string,
    resourceName: string,
    payload: TPayload,
    options: Partial<BrowserLocalCachePutOptions> = {}
  ): Promise<BrowserLocalCacheRecord<TPayload>> {
    const record = buildLocalCacheRecord(this.namespace, key, resourceName, payload, {
      ttlMs: options.ttlMs ?? BROWSER_LOCAL_CACHE_DEFAULT_API_TTL_MS,
      ...options,
    })
    await this.driver.put(storeName, key, record)
    return record
  }

  async delete(storeName: BrowserLocalCacheStoreName, key: string): Promise<void> {
    await this.driver.delete(storeName, key)
  }

  async clearCurrentUser(storeName: BrowserLocalCacheStoreName): Promise<number> {
    return await this.driver.clearUserScope(storeName, this.namespace.userScope)
  }
}

